import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import type { DependencyList, RefObject } from 'react';

gsap.registerPlugin(ScrollTrigger, ScrollSmoother, ScrollToPlugin, useGSAP);

export { gsap, ScrollSmoother, ScrollTrigger };

/** Inertia scrolling only for mouse/trackpad users who haven't asked for reduced motion. */
export const SMOOTH_QUERY = '(pointer: fine) and (prefers-reduced-motion: no-preference)';
export const MOTION_QUERY = '(prefers-reduced-motion: no-preference)';

export const prefersMotion = () => window.matchMedia(MOTION_QUERY).matches;

/** Height of the fixed site header plus breathing room, so sections don't land under it. */
export function headerOffset() {
  const header = document.querySelector<HTMLElement>('[data-site-header]');
  return (header?.offsetHeight ?? 80) + 16;
}

/**
 * Glide to an element, selector or y position.
 * Uses ScrollSmoother when it is running, GSAP ScrollTo on touch devices, and jumps when motion is reduced.
 */
export function scrollToTarget(target: string | Element | number, { instant = false } = {}) {
  const smoother = ScrollSmoother.get();
  const el = typeof target === 'string' ? document.querySelector(target) : target;
  if (el === null) return;

  if (instant || !prefersMotion()) {
    const y =
      typeof el === 'number' ? el : el.getBoundingClientRect().top + window.scrollY - headerOffset();
    if (smoother) smoother.scrollTop(y);
    else window.scrollTo(0, y);
    return;
  }

  if (smoother) {
    smoother.scrollTo(el, true, typeof el === 'number' ? undefined : `top ${headerOffset()}px`);
    return;
  }
  gsap.to(window, {
    duration: 1.1,
    ease: 'power3.inOut',
    scrollTo: typeof el === 'number' ? { y: el } : { y: el, offsetY: headerOffset(), autoKill: true },
  });
}

/** Freezes page scrolling under drawers and sheets (pauses the smoother too). */
export function lockScroll(locked: boolean) {
  ScrollSmoother.get()?.paused(locked);
  document.documentElement.style.overflow = locked ? 'hidden' : '';
}

/**
 * Keeps an element pinned beside a taller sibling column on desktop (replaces CSS sticky,
 * which doesn't survive ScrollSmoother's transforms).
 */
export function usePinOnDesktop(
  pin: RefObject<HTMLElement | null>,
  within: RefObject<HTMLElement | null>,
  dependencies: DependencyList = [],
) {
  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add('(min-width: 1024px)', () => {
        const el = pin.current;
        const container = within.current;
        if (!el || !container) return;
        ScrollTrigger.create({
          trigger: el,
          pin: el,
          pinSpacing: false,
          // Page effects run before the layout creates the smoother, so choose the pin type by the same rule.
          pinType: window.matchMedia(SMOOTH_QUERY).matches ? 'transform' : 'fixed',
          start: () => `top ${headerOffset() + 16}px`,
          end: () => {
            const padding = parseFloat(getComputedStyle(container).paddingBottom) || 0;
            const offset = el.getBoundingClientRect().top - container.getBoundingClientRect().top;
            return `+=${Math.max(0, container.offsetHeight - padding - el.offsetHeight - offset)}`;
          },
          invalidateOnRefresh: true,
        });
      });
      return () => mm.revert();
    },
    { dependencies: [...dependencies], revertOnUpdate: true },
  );
}
